import React from 'react';
import {
    Config,
    formatPrice,
    I18nContext,
    StockLabel,
    truncatePrice,
} from '@deporvillage/utils-library-component';
import {
    CriteoOneTag,
    FacebookPixel,
    Optimize,
    OneTrust,
    Sfmc,
    TrackingLibrary,
    PixelCampaign,
} from '@deporvillage/frontend-common';
import Head from 'next/head';
import { cdn } from '@deporvillage/picture-component';

const getTrackingConfig = ({
    tracking, ebus, optimize, environment,
}) => ({
    analytics: {
        name: 'Analytics',
        config: {
            analytics: {
                version: tracking?.analytics?.version,
                code: tracking?.analytics?.code,
            },
            ga4: {
                code:
                    ebus?.providers?.ga4?.config &&
                    ebus.providers.ga4.config[environment]?.code,
                como:
                    (ebus?.providers?.ga4?.config &&
                        ebus?.providers?.ga4?.config[environment]?.como) ||
                    null,
            },
            adwords: {
                code: tracking?.adwords?.code,
            },
            optimize: {
                enabled: optimize.enabled,
                code: optimize.code,
            },
            tagManager: {},
        },
    },
    deporvillage: {
        name: ebus?.providers?.deporvillage?.name,
        config: ebus?.providers?.deporvillage?.config[environment],
    },
});

// toDo: price bot FF
const priceModifier = ({ threshold, value, isBot }) => (
    !!isBot * value * (1 + threshold / 100) || value
);

const ProductTemplate = ({ children, model }) => {
    const {
        title: modelTitle,
        meta_title: metaTitle,
        canonical,
        url: pageUrl,
        gallery_images: gallery,
        default_variant: defaultVariant,
        root_category: categoryId,
        isIndexable,
        sku,
        brand,
        rating,
        breadcrumb,
    } = model;

    const {
        lang, ebus, environment, oneTrust, tracking, optimize, facebook,
        page, sfmc, store, storeId, country, url, criteo,
        cdn: cdnConfig, locale, extension, stores, pixelCampaign, isBot,
        isMobile, featureFlags,
    } = React.useContext(Config);

    const { translate } = React.useContext(I18nContext);
    const canonicalUrl = canonical.find((c) => c.lang === lang);

    const pixelCampaignCategory = pixelCampaign?.enabled &&
        pixelCampaign?.page?.categoryPage?.find(({ id }) => +id === categoryId)?.category;

    const trackingProviders = getTrackingConfig({
        tracking, ebus, optimize, environment,
    });

    const title = translate('meta_data.product.title', {
        product_name: metaTitle || modelTitle,
    });

    const description = translate('meta_data.product.description', {
        product_name: modelTitle,
        product_price: formatPrice(
            defaultVariant.price.price,
            defaultVariant.price.currency_code,
            lang,
        ),
    });

    /* istanbul ignore next */
    const price = {
        price: priceModifier({
            threshold: featureFlags?.enableBotPrice?.threshold,
            value: defaultVariant?.price?.price,
            isBot,
        }),
        currency: defaultVariant?.price?.currency_code,
    };

    /* istanbul ignore next */
    const outOfStock = defaultVariant.stockLabel === StockLabel.NoStock &&
        !defaultVariant.replenishment;

    /* istanbul ignore next */
    const breadcrumbItems = [{
        url: '/',
        name: translate('generic.home'),
    }, /* istanbul ignore next */ ...(breadcrumb || [])].map((i, k, a) => (
        { ...i, hidden: isMobile && !(/* istanbul ignore next */ a.length - 3 < k) }
    ));

    return (
        <>
            <Head>
                <title key={'title'}>{title}</title>
                <meta name='description' content={description} />

                <link rel="schema.DC" href="http://purl.org/dc/elements/1.1/" type="text/plain"/>
                <link rel="schema.DCTERMS" href="http://purl.org/dc/terms/" type="text/plain"/>
                <meta name='DC.title' lang={lang} content={title} />
                <meta name='DC.description' lang={lang} content={description} />
                {/* eslint-disable-next-line react/no-unknown-property */}
                <meta name='DC.identifier' scheme='DCTERMS.URI' content={`https:${url}`} />
                {/* eslint-disable-next-line react/no-unknown-property */}
                <meta name='DC.type' scheme='DCTERMS.DCMIType' content='Text' />
                <meta name='DC.format' content='text/html; charset=utf-8' />
                {/* eslint-disable-next-line react/no-unknown-property */}
                <meta name='DC.language' scheme='ISO639-1' content={lang} />

                <meta property='og:title' content={title} />
                <meta property='og:description' content={description} />
                <meta property='og:locale' content={locale} />
                <meta property='og:site_name' content={`deporvillage${extension}`} />
                <meta property='og:url' content={`https:${url}${/* istanbul ignore next */ canonicalUrl?.url || pageUrl}`} />

                {
                    gallery && (
                        <meta
                            property='og:image'
                            content={cdn({ src: `/product/${gallery.images[gallery.default]}` }, cdnConfig)}
                        />
                    )
                }

                {
                    canonical.map((c) => {
                        const store = stores.find((s) => s.lang === c.lang);
                        /* istanbul ignore next */
                        if (pageUrl === canonicalUrl?.url) {
                            /* istanbul ignore next */
                            return c.lang === lang ? (
                                <React.Fragment key={c.lang}>
                                    <link rel='canonical' href={`https:${store.url}${c.url}`} />
                                    <link rel='alternate' href={`https:${store.url}${c.url}`} hrefLang={c.lang} />
                                </React.Fragment>
                            ) : (
                                <link key={c.lang} rel='alternate' href={`https:${store.url}${c.url}`} hrefLang={c.lang} />
                            );
                        }
                        return (c.lang === lang && (
                            <link key={c.lang} rel='canonical' href={`https:${store.url}${c.url}`} />
                        )) || (
                            null
                        );
                    })
                }

                <meta
                    name='robots'
                    content={!isIndexable || country.code !== store.code ? 'noindex,nofollow' : 'index,follow'}
                />

                <script
                    data-testid="product-ldjson"
                    type='application/ld+json'
                    dangerouslySetInnerHTML={{
                        __html: `
                            {
                                "@context": "https://schema.org",
                                "@type": "WebPage",
                                "breadcrumb": {
                                    "@type": "BreadcrumbList",
                                    "itemListElement": [
                                        ${breadcrumbItems.map((item, i) => item?.url && (
                                                `
                                                {
                                                    "@type": "ListItem",
                                                    "position": "${i + 1}",
                                                    "item": {
                                                        "@type": "Thing",
                                                        "@id": "${url}${item.url}",
                                                        "name": "${item.name}"
                                                    }
                                                }
                                                `
                                        ))}
                                    ]
                                },
                                "mainEntity": {
                                    "@type": "Product",
                                    "name": "${modelTitle.replaceAll('"', '\\"')}",
                                    "image": "${cdn({ src: `product/${gallery.images[gallery.default]}` }, cdnConfig)}",
                                    "description": "${description.replaceAll('"', '\\"')}",
                                    "sku": "${sku}",
                                    "brand": {
                                        "@type": "Brand",
                                        "name": "${brand?.name}"
                                    },
                                    ${rating?.rate ? (
                                        `"aggregateRating": {
                                        "@type": "AggregateRating",
                                        "ratingValue": "${(rating?.rate).toFixed(1)}",
                                        "worstRating": "0.0",
                                        "bestRating": "5",
                                        "reviewCount": "${rating?.count}"
                                    },`
                                    ) : ''}
                                    "offers": {
                                        "@type": "Offer",
                                        "priceCurrency": "${price.currency}",
                                        "itemCondition": "https://schema.org/NewCondition",
                                        "availability": "${ /* istanbul ignore next */ !outOfStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock'}",
                                        "url": "${url}${pageUrl}",
                                        "price": "${truncatePrice(price.price, price.currency)}"
                                    }
                                }
                            }`,
                    }}
                />
            </Head>

            {
                optimize && (
                    <Optimize code={optimize.code} />
                )
            }

            {
                oneTrust && oneTrust.enabled && (
                    <OneTrust
                        id={oneTrust.id}
                        lang={lang}
                        script={oneTrust.script}
                    />
                )
            }

            {
                facebook && facebook.enabled && (
                    <FacebookPixel
                        page={page}
                        currency={country.currency_code}
                        product={{
                            sku: defaultVariant.sku,
                            price: defaultVariant.price.price,
                        }}
                    />
                )
            }

            {
                criteo && criteo.enabled && (
                    <CriteoOneTag
                        id={criteo.accountId}
                        script={criteo.script}
                        page={page}
                        model={model}
                    />
                )
            }

            {
                sfmc && sfmc.enabled && sfmc?.originId && (
                    <Sfmc id={sfmc.originId} model={model} page={page} />
                )
            }

            {
                ebus?.bucket && (
                    <TrackingLibrary
                        providers={trackingProviders}
                        bucket={ebus.bucket[environment]}
                        breadcrumb={model.breadcrumb}
                        storeId={storeId}
                    />
                )
            }

            {
                pixelCampaignCategory && (
                    <PixelCampaign category={pixelCampaignCategory} />
                )
            }

            <div>
                {children}
            </div>
        </>
    );
};

export default ProductTemplate;
